import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText } from "lucide-react";

const SECTIONS = [
  ["1. About Reefer Sathi", "Reefer Sathi is a listing and matching board that lets cold-chain transporters post load and truck availability and connect with each other. We provide the platform only — we are not a party to, broker of, or guarantor of any transport contract, rate, or payment agreed between members."],
  ["2. Eligibility & Accounts", "You must be a genuine transporter, fleet owner, or shipper representative and provide accurate business and contact details. You are responsible for all activity under your account and for keeping your login secure."],
  ["3. Subscription & Free Trial", "New members get a 60-day free trial. After the trial, continued access requires an active paid subscription. Fees are billed as shown at checkout and are non-refundable except where required by law. You may cancel anytime; access continues until the end of the paid period."],
  ["4. Member Conduct", "You agree to post only real, current loads and trucks with honest details (location, temperature range, capacity, weight and dates). No spam, fake postings, harassment, or misuse of other members' contact details. We may suspend or remove accounts and postings that breach these rules."],
  ["5. Deals Between Members", "Any booking, rate, advance, cargo condition, insurance, or dispute is strictly between the members involved. Reefer Sathi does not verify cargo, vehicles, or payments and is not liable for any loss, damage, delay, spoilage, non-payment, or fraud arising from a deal made through the platform. Verified badges indicate a manual admin check only and are not a guarantee."],
  ["6. Contact & Communications", "By posting, you consent to other members contacting you via the phone/WhatsApp number and in-app messaging you provide, for the purpose of arranging freight."],
  ["7. Data", "We store the details you provide to operate the service. Do not post sensitive personal data of third parties. You are responsible for the legality of the information you share."],
  ["8. Liability", "The service is provided 'as is'. To the maximum extent permitted by law, Reefer Sathi is not liable for indirect or consequential losses, and our total liability is limited to the subscription fees you paid in the preceding month."],
  ["9. Changes", "We may update these terms and will show the current version here. Continued use after an update means you accept the revised terms."],
];

export default function TermsDialog({ open, onOpenChange, onAccept, readOnly = false }) {
  const [checked, setChecked] = useState(false);

  const accept = () => {
    if (readOnly) { onOpenChange(false); return; }
    if (!checked) return;
    try { localStorage.setItem("frigoload_terms_accepted", new Date().toISOString()); } catch (e) { console.error(e); }
    onOpenChange(false);
    onAccept?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" /> Terms & Conditions
          </DialogTitle>
          <DialogDescription>Please read and accept before you continue.</DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[46vh] pr-4 rounded-lg border border-border bg-background/40 p-4">
          <div className="space-y-4">
            {SECTIONS.map(([title, body]) => (
              <div key={title}>
                <h4 className="font-heading font-semibold text-sm text-foreground mb-1">{title}</h4>
                <p className="text-xs leading-relaxed text-muted-foreground">{body}</p>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground/70 pt-2">Last updated: June 2026</p>
          </div>
        </ScrollArea>

        {!readOnly && (
          <label className="flex items-start gap-3 cursor-pointer select-none pt-1" data-testid="terms-agree-label">
            <Checkbox data-testid="terms-agree-checkbox" checked={checked} onCheckedChange={(v) => setChecked(!!v)} className="mt-0.5" />
            <span className="text-sm text-muted-foreground">I have read and agree to the Terms & Conditions of Reefer Sathi.</span>
          </label>
        )}

        <DialogFooter>
          <Button
            data-testid="terms-continue-btn"
            onClick={accept}
            disabled={!readOnly && !checked}
            className="w-full rounded-full font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {readOnly ? "Close" : "Agree & Continue with Google"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
